// Advanced section transition animations with proper TypeScript types
interface HeroAnimation {
  duration: number;
  easing: string;
  exitHero: (heroElement: HTMLElement) => void;
  enterNext: (nextElement: HTMLElement) => void;
  cleanup: (heroElement: HTMLElement, nextElement: HTMLElement) => void;
}

interface SectionAnimation {
  duration: number;
  easing: string;
  exitCurrent: (currentElement: HTMLElement) => void;
  enterNext: (nextElement: HTMLElement) => void;
  cleanup: (currentElement: HTMLElement, nextElement: HTMLElement) => void;
}

export const sectionTransitions = {
  // Hero to next section transition
  heroToNext: {
    duration: 1200,
    easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    
    exitHero: (heroElement: HTMLElement) => {
      heroElement.style.transition = `all 0.8s cubic-bezier(0.4, 0.0, 0.2, 1)`;
      heroElement.style.transform = 'translateY(-50px) scale(0.95)';
      heroElement.style.opacity = '0.7';
      
      // Add sparkle effect
      const sparkles = document.createElement('div');
      sparkles.className = 'absolute inset-0 pointer-events-none';
      sparkles.innerHTML = `
        <div class="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
        <div class="absolute top-1/3 right-1/4 w-1 h-1 bg-blue-400 rounded-full animate-pulse delay-200"></div>
        <div class="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-green-400 rounded-full animate-ping delay-500"></div>
      `;
      heroElement.appendChild(sparkles);
      
      // Remove sparkles after animation
      setTimeout(() => {
        sparkles.remove();
      }, 1000);
    },
    
    enterNext: (nextElement: HTMLElement) => {
      nextElement.style.transform = 'translateY(30px)';
      nextElement.style.transition = 'all 1s cubic-bezier(0.4, 0.0, 0.2, 1)';
      nextElement.style.opacity = '0.8';
      
      // Add glow effect
      nextElement.style.boxShadow = '0 0 50px rgba(198, 161, 91, 0.3)';
      
      setTimeout(() => {
        nextElement.style.transform = 'translateY(0)';
        nextElement.style.opacity = '1';
        nextElement.style.boxShadow = '';
      }, 100);
    },
    
    cleanup: (heroElement: HTMLElement, nextElement: HTMLElement) => {
      setTimeout(() => {
        heroElement.style.transform = '';
        heroElement.style.opacity = '';
        heroElement.style.transition = '';
        nextElement.style.transition = '';
        nextElement.style.boxShadow = '';
      }, 1200);
    }
  } as HeroAnimation,
  
  // Generic section to section transition
  sectionToSection: {
    duration: 1000,
    easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    
    exitCurrent: (currentElement: HTMLElement) => {
      currentElement.style.transition = 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      currentElement.style.transform = 'scale(0.98) translateY(-20px)';
      currentElement.style.opacity = '0.6';
    },
    
    enterNext: (nextElement: HTMLElement) => {
      nextElement.style.transform = 'translateY(40px)';
      nextElement.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      nextElement.style.opacity = '0.7';
      
      setTimeout(() => {
        nextElement.style.transform = 'translateY(0)';
        nextElement.style.opacity = '1';
      }, 50);
    },
    
    cleanup: (currentElement: HTMLElement, nextElement: HTMLElement) => {
      setTimeout(() => {
        currentElement.style.transform = '';
        currentElement.style.opacity = '';
        currentElement.style.transition = '';
        nextElement.style.transition = '';
      }, 1000);
    }
  } as SectionAnimation
};

// Enhanced scroll to section with animations
export const scrollToSectionWithAnimation = (
  targetSectionId: string, 
  currentSectionId?: string,
  animationType: 'heroToNext' | 'sectionToSection' = 'sectionToSection'
) => {
  const targetSection = document.querySelector(targetSectionId) as HTMLElement;
  const currentSection = currentSectionId ? document.querySelector(currentSectionId) as HTMLElement : null;
  
  if (!targetSection) return false;
  
  if (animationType === 'heroToNext') {
    const animation = sectionTransitions.heroToNext;
    
    // Apply exit animation to current section
    if (currentSection) {
      const hero = currentSection.querySelector('.abask-hero') as HTMLElement;
      if (hero) {
        animation.exitHero(hero);
      }
    }
    
    // Apply entrance animation to target section
    animation.enterNext(targetSection);
    
    // Perform the scroll
    setTimeout(() => {
      targetSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
      
      // Cleanup animations
      setTimeout(() => {
        if (currentSection) {
          const hero = currentSection.querySelector('.abask-hero') as HTMLElement;
          if (hero) {
            animation.cleanup(hero, targetSection);
          }
        }
      }, animation.duration);
    }, 200);
  } else {
    const animation = sectionTransitions.sectionToSection;
    
    // Apply exit animation to current section
    if (currentSection) {
      animation.exitCurrent(currentSection);
    }
    
    // Apply entrance animation to target section
    animation.enterNext(targetSection);
    
    // Perform the scroll
    setTimeout(() => {
      targetSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
      
      // Cleanup animations
      setTimeout(() => {
        if (currentSection) {
          animation.cleanup(currentSection, targetSection);
        }
      }, animation.duration);
    }, 200);
  }
  
  return true;
};

// Utility to add parallax effect during scroll
export const addParallaxEffect = (element: HTMLElement, intensity: number = 0.5) => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const rect = entry.boundingClientRect;
        const scrolled = window.pageYOffset;
        const rate = scrolled * intensity;
        element.style.transform = `translateY(${rate}px)`;
      }
    });
  });
  
  observer.observe(element);
  return observer;
};